import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
    PatientPreconfig,
    PatientPreconfigDetail,
    PreconfigInquiry,
    PreconfigSheetParameter,
    Preconfiguration
} from '../interfaces/Preconfigurations';
import { useNavigate } from "react-router-dom";
import { useToast } from "./useToast";
import useHttp from "./useHttp";
import {
    patientPreconfigReducer,
    PreconfigDispatch,
    preconfigInquiriesReducer,
    preconfigPatientInquiriesReducer
} from "../reducers/preconfig-reducer";
import { getEnvironmentApiUrl } from "../services/General";
import { useTranslation } from "react-i18next";
import { allNullFrequencyValues } from "../services/getPollAttribute";
import { Parameter } from "../interfaces/Profile";
import { Column } from "../interfaces/Columns";
import ButtonsTablePreconfig from "../components/PreconfigurationPage/ButtonsTablePreconfig/ButtonsTablePreconfig";
import PreconfigPathologies from "../components/PreconfigurationPage/PreconfigPathologies/PreconfigPathologies";
import EnvironmentsString from "../components/Helpers/EnvironmentsString";
import AssignPreconfigButton from "../components/PreconfigurationPage/AssignPreconfigButton/AssignPreconfigButton";
import { LinkedInquiry, PatientInquiry, Poll, PollFrequency } from "../interfaces/Poll";
import { OptionAutocomplete } from "../interfaces/Input";
import { HH, mm } from "../interfaces/Time";
import { parseReminderTime } from "../services/util";
import {
    toPatientPreconfigOverridePayload,
    toPreconfigFormData,
    toPreconfigUpsertPayload
} from "../adapters/preconfigAdapter";

enum CreatePreconfigErrors {
    NAME_REQUIRED = "NAME_REQUIRED",
    PROFILE_REQUIRED = "PROFILE_REQUIRED",
    PARAMETER_LIMITS_INVALID = "PARAMETER_LIMITS_INVALID",
    PARAMETER_EMPTY = "PARAMETER_EMPTY",
    FREQUENCY_VALUE_REQUIRED = "FREQUENCY_VALUE_REQUIRED"
}

export interface PreconfigErrors {
    [CreatePreconfigErrors.NAME_REQUIRED]?: boolean,
    [CreatePreconfigErrors.PROFILE_REQUIRED]?: boolean,
    [CreatePreconfigErrors.PARAMETER_LIMITS_INVALID]?: { [key: string]: boolean }
    [CreatePreconfigErrors.PARAMETER_EMPTY]?: { [key: string]: boolean }
    [CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED]?: { [key: string]: boolean }
}

interface PreconfigDataState {
    name: string
    selectedPathologiesIDs: number[]
    selectedProfilesIDs: number[]
}

type AssignedCaseResponse = {
    id: number | string
}

export type PreconfigDataDispatch =
    { type: 'CHANGE_NAME', payload: string } |
    { type: 'ADD_PATHOLOGY', payload: number } |
    { type: 'SET_PATHOLOGIES', payload: number[] } |
    { type: 'REMOVE_PATHOLOGY', payload: number } |
    { type: 'ADD_PROFILE', payload: number } |
    { type: 'SET_PROFILES', payload: number[] }

type PreconfigFrequencyItem = PreconfigInquiry | PreconfigSheetParameter;

const isSheetParameterItem = (
    item: PreconfigFrequencyItem
): item is PreconfigSheetParameter => {
    return 'sheet_parameter_id' in item;
};

const getFrequencyItemKey = (item: PreconfigFrequencyItem, fallback: string) => {
    if (isSheetParameterItem(item)) {
        return String(item.sheet_parameter_id ?? fallback);
    }

    return String(item.inquiry_id ?? fallback);
};

const hasFrequencyValueMissing = (item: PreconfigFrequencyItem) => {
    if (allNullFrequencyValues(item)) return false;

    return item.frecuency === null || item.frecuency === undefined;
};

const preconfigDataReducer = (state: PreconfigDataState, action: PreconfigDataDispatch): PreconfigDataState => {
    if (action.type === 'CHANGE_NAME') return { ...state, name: action.payload };

    if (action.type === 'ADD_PATHOLOGY') {
        const pathologyExists = state.selectedPathologiesIDs.some(id => id === action.payload);
        return {
            ...state,
            selectedPathologiesIDs: pathologyExists
                ? state.selectedPathologiesIDs.filter(id => id !== action.payload)
                : [...state.selectedPathologiesIDs, action.payload]
        };
    }

    if (action.type === 'SET_PATHOLOGIES') return { ...state, selectedPathologiesIDs: action.payload };

    if (action.type === 'REMOVE_PATHOLOGY') {
        return {
            ...state,
            selectedPathologiesIDs: state.selectedPathologiesIDs.filter(id => id !== action.payload)
        };
    }

    if (action.type === 'ADD_PROFILE') {
        const profileExists = state.selectedProfilesIDs.some(id => id === action.payload);
        return {
            ...state,
            selectedProfilesIDs: profileExists
                ? state.selectedProfilesIDs.filter(id => id !== action.payload)
                : [...state.selectedProfilesIDs, action.payload]
        };
    }

    if (action.type === 'SET_PROFILES') {
        return { ...state, selectedProfilesIDs: action.payload };
    }

    return state;
};

export const useCreatePreconfig = (defaultPreconfig: Preconfiguration | undefined) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState<PreconfigErrors>({});

    const defaultFormData = useMemo(() => toPreconfigFormData(defaultPreconfig), [defaultPreconfig]);

    const [preconfigState, dispatchPreconfig] = useReducer(preconfigDataReducer, {
        name: defaultFormData.name,
        selectedPathologiesIDs: defaultFormData.pathologyIds,
        selectedProfilesIDs: defaultFormData.profileIds,
    });

    const { name, selectedPathologiesIDs, selectedProfilesIDs } = preconfigState;

    const navigate = useNavigate();
    const toast = useToast();
    const { sendRequest } = useHttp();

    const [preconfigInquiries, dispatchPreconfigInquiries] = useReducer(
        preconfigInquiriesReducer,
        defaultFormData.inquiries
    );

    const [preconfigSheetParameters, dispatchPreconfigSheetParams] = useReducer(
        patientPreconfigReducer,
        defaultFormData.sheetParameters
    );

    const postPreconfig = useCallback((preconfigBody: ReturnType<typeof toPreconfigUpsertPayload>) => {
        const isEdit = !!defaultPreconfig?.id;

        sendRequest({
            url: isEdit
                ? getEnvironmentApiUrl() + 'preconfig/' + defaultPreconfig.id
                : getEnvironmentApiUrl() + 'preconfig',
            method: isEdit ? 'PUT' : 'POST',
            body: preconfigBody
        }, () => {
            navigate('/home/administration/preconfiguration');
        }, () => {
            toast.error(t('error_creating_preconfiguration'));
        });
    }, [defaultPreconfig, navigate, sendRequest, t, toast]);

    const checkErrors = () => {
        const errorsAux: PreconfigErrors = {};

        if (name.trim() === '') {
            errorsAux[CreatePreconfigErrors.NAME_REQUIRED] = true;
        }

        if (selectedProfilesIDs.length === 0) {
            errorsAux[CreatePreconfigErrors.PROFILE_REQUIRED] = true;
        }

        for (let i = 0; i < preconfigSheetParameters.length; i++) {
            const param = preconfigSheetParameters[i];
            const minLimit = param.min_limit ?? null;
            const maxLimit = param.max_limit ?? null;
            const key = getFrequencyItemKey(param, `sheet-${i}`);

            const hasNoLimits = minLimit === null && maxLimit === null;
            const hasNoReminder = param.reminder_time === null;
            const hasNoFrequency = allNullFrequencyValues(param);
            const hasIncompleteFrequency = hasFrequencyValueMissing(param);

            if (hasNoFrequency || hasIncompleteFrequency) {
                errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED] = {
                    ...errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED],
                    [key]: true
                };
            } else if (hasNoLimits && hasNoReminder) {
                errorsAux[CreatePreconfigErrors.PARAMETER_EMPTY] = {
                    ...errorsAux[CreatePreconfigErrors.PARAMETER_EMPTY],
                    [key]: true
                };
            } else if (
                minLimit !== null &&
                maxLimit !== null &&
                Number(minLimit) > Number(maxLimit)
            ) {
                errorsAux[CreatePreconfigErrors.PARAMETER_LIMITS_INVALID] = {
                    ...errorsAux[CreatePreconfigErrors.PARAMETER_LIMITS_INVALID],
                    [key]: true
                };
            }
        }

        for (let i = 0; i < preconfigInquiries.length; i++) {
            const inquiry = preconfigInquiries[i];
            const key = getFrequencyItemKey(inquiry, `inquiry-${i}`);
            const hasNoFrequency = allNullFrequencyValues(inquiry);

            if (hasNoFrequency) {
                errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED] = {
                    ...errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED],
                    [key]: true
                };
            } else if (hasFrequencyValueMissing(inquiry)) {
                errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED] = {
                    ...errorsAux[CreatePreconfigErrors.FREQUENCY_VALUE_REQUIRED],
                    [key]: true
                };
            }
        }

        return errorsAux;
    };

    const handleSave = () => {
        const errorsAux = checkErrors();

        setErrors(errorsAux);

        if (Object.keys(errorsAux).length > 0) {
            return errorsAux;
        }

        const preconfigBody = toPreconfigUpsertPayload({
            name,
            profileIds: selectedProfilesIDs,
            pathologyIds: selectedPathologiesIDs,
            inquiries: preconfigInquiries,
            sheetParameters: preconfigSheetParameters,
        });

        postPreconfig(preconfigBody);
        return {};
    };

    return {
        errors,
        name,
        selectedPathologiesIDs,
        selectedProfilesIDs,
        preconfigSheetParameters,
        preconfigInquiries,
        dispatchPreconfig,
        dispatchPreconfigSheetParams,
        dispatchPreconfigInquiries,
        handleSave,
    };
};

export const useFetchPreconfigData = () => {
    const { sendRequest } = useHttp<Parameter[]>();
    const [sheetParameters, setSheetParameters] = useState<Parameter[]>([]);

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'sheet_parameter/device_parameters',
            method: 'POST',
        }, (params) => {
            setSheetParameters(params);
        });
    }, [sendRequest]);

    return { sheetParameters };
};

export const useAssignPreconfig = (preconfigID: number, closeModal: () => void) => {
    const { t } = useTranslation();
    const { error: errorToast } = useToast();

    const { sendRequest } = useHttp();
    const { isLoading, error, sendRequest: getAssignedCases } = useHttp<AssignedCaseResponse[]>();
    const { sendRequest: getCurrentPreconfigCases } = useHttp<AssignedCaseResponse[]>();

    const [assignedCaseIds, setAssignedCaseIds] = useState<number[]>([]);
    const [currentPreconfigCaseIds, setCurrentPreconfigCaseIds] = useState<number[]>([]);

    useEffect(() => {
        getAssignedCases({
            url: getEnvironmentApiUrl() + 'preconfig/assigned_cases',
            method: 'GET',
        }, (data) => {
            const currentAssignedCaseIds = [
                ...new Set(
                    (data ?? [])
                        .map((item) => Number(item.id))
                        .filter((caseId) => !Number.isNaN(caseId))
                )
            ];

            setAssignedCaseIds(currentAssignedCaseIds);
        });

        getCurrentPreconfigCases({
            url: getEnvironmentApiUrl() + 'preconfig/assigned_cases?preconfig_id=' + preconfigID,
            method: 'GET',
        }, (data) => {
            const currentCaseIds = [
                ...new Set(
                    (data ?? [])
                        .map((item) => Number(item.id))
                        .filter((caseId) => !Number.isNaN(caseId))
                )
            ];

            setCurrentPreconfigCaseIds(currentCaseIds);
        });
    }, [getAssignedCases, getCurrentPreconfigCases, preconfigID]);

    const assign = useCallback((caseIds: number[]) => {
        if (caseIds.length === 0) {
            closeModal();
            return;
        }

        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/' + preconfigID + '/assign_cases',
            method: 'POST',
            body: caseIds
        }, () => {
            closeModal();
        }, () => {
            errorToast(t('patients_not_assigned_correctly'));
        });
    }, [sendRequest, preconfigID, closeModal, errorToast, t]);

    const unAssign = useCallback((caseIds: number[]) => {
        if (caseIds.length === 0) {
            closeModal();
            return;
        }

        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/' + preconfigID + '/unassign_cases',
            method: 'PUT',
            body: caseIds
        }, () => {
            closeModal();
        }, () => {
            errorToast(t('patients_not_unassigned_correctly'));
        });
    }, [sendRequest, preconfigID, closeModal, errorToast, t]);

    return {
        isLoading,
        error,
        assignedCaseIds,
        currentPreconfigCaseIds,
        assign,
        unAssign
    };
};

export const usePreconfigurations = () => {
    const { t } = useTranslation();
    const { isLoading, error, sendRequest } = useHttp<Preconfiguration[]>();
    const { sendRequest: getAssignedCases } = useHttp<AssignedCaseResponse[]>();

    const [preconfigs, setPreconfigs] = useState<Preconfiguration[]>([]);
    const [linkedCaseIdsByPreconfig, setLinkedCaseIdsByPreconfig] = useState<Record<number, number[]>>({});

    const fetchLinkedCasesByPreconfig = useCallback((preconfigsList: Preconfiguration[]) => {
        preconfigsList.forEach((preconfig) => {
            getAssignedCases({
                url: getEnvironmentApiUrl() + 'preconfig/assigned_cases?preconfig_id=' + preconfig.id,
                method: 'GET',
            }, (assignedCases) => {
                const currentCaseIds = [
                    ...new Set(
                        (assignedCases ?? [])
                            .map((item) => Number(item.id))
                            .filter((caseId) => !Number.isNaN(caseId))
                    )
                ];

                setLinkedCaseIdsByPreconfig((prev) => ({
                    ...prev,
                    [preconfig.id]: currentCaseIds
                }));
            });
        });
    }, [getAssignedCases]);

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/all',
            method: 'POST',
            body: { params: {}, filter: {} }
        }, (data) => {
            const filteredPreconfigs = data.filter(p => !p.is_patient_override);
            setPreconfigs(filteredPreconfigs);
            fetchLinkedCasesByPreconfig(filteredPreconfigs);
        });
    }, [fetchLinkedCasesByPreconfig, sendRequest]);

    const refreshPreconfigAssignments = useCallback(() => {
        fetchLinkedCasesByPreconfig(preconfigs);
    }, [fetchLinkedCasesByPreconfig, preconfigs]);

    const handleAddPreconfig = (preconfig: Preconfiguration) =>
        setPreconfigs(oldPreconfigs => [...oldPreconfigs, preconfig]);

    const handleDeletedPreconfig = (preconfigID: Preconfiguration['id']) =>
        setPreconfigs(oldPreconfigs => oldPreconfigs.filter(p => p.id !== preconfigID));

    const PRECONFIG_COLUMNS: Column<Preconfiguration>[] = [
        {
            id: 0,
            name: () => t('actions'),
            visible: true,
            sortable: false,
            element: (preconfig: Preconfiguration) =>
                <ButtonsTablePreconfig
                    preconfiguration={preconfig}
                    addPreconfig={handleAddPreconfig}
                    deletePreconfig={handleDeletedPreconfig}
                    caseIdsLinked={linkedCaseIdsByPreconfig[preconfig.id] ?? []}
                />
        },
        {
            id: 1,
            name: () => t('name'),
            visible: true,
            sortable: true,
            cell: (preconfig: Preconfiguration) => preconfig.name
        },
        {
            id: 2,
            name: () => t('tags'),
            visible: true,
            sortable: false,
            element: (preconfig: Preconfiguration) =>
                <PreconfigPathologies
                    preconfigPathologies={(preconfig.pathologies ?? []).map(p => ({
                        pathology_id: p.id,
                        pathology: p
                    }))}
                />
        },
        {
            id: 3,
            name: () => t('environment'),
            visible: true,
            sortable: false,
            element: (preconfig: Preconfiguration) =>
                preconfig.profiles?.length
                    ? <EnvironmentsString profileIDs={preconfig.profiles.map(pp => pp.id)} />
                    : <></>
        },
        {
            id: 4,
            name: () => '',
            visible: true,
            sortable: false,
            classBody: 'center',
            element: (preconfig: Preconfiguration) =>
                <AssignPreconfigButton
                    assign={true}
                    preconfig={preconfig}
                    onAssigned={refreshPreconfigAssignments}
                />
        },
        {
            id: 5,
            name: () => '',
            visible: true,
            sortable: false,
            classBody: 'center',
            element: (preconfig: Preconfiguration) =>
                <AssignPreconfigButton
                    assign={false}
                    preconfig={preconfig}
                    onAssigned={refreshPreconfigAssignments}
                />
        },
    ];

    return {
        isLoading,
        error,
        preconfigs,
        columns: PRECONFIG_COLUMNS,
        refreshPreconfigAssignments
    };
};

export const useFetchPatientPreconfig = (
    patientPreconfigID: PatientPreconfig['id'],
    patientID: number
) => {
    const { isLoading, error, sendRequest: getPatientPreconfig } = useHttp<PatientPreconfigDetail>();
    const {
        isLoading: loadingInquiry,
        error: errorPatientInquiry,
        sendRequest: getPatientInquries
    } = useHttp<LinkedInquiry[]>();

    const [patientPreconfigDetail, setPatientPreconfigDetail] = useState<PatientPreconfigDetail | null>(null);
    const [patientInquiries, setPatientInquiries] = useState<LinkedInquiry[]>([]);

    const fetchPatientPreconfigData = useCallback(() => {
        return new Promise<{
            detail: PatientPreconfigDetail;
            inquiries: LinkedInquiry[];
        }>((resolve, reject) => {
            if (patientPreconfigID == null) {
                reject(new Error('patientPreconfigID is null'));
                return;
            }

            getPatientPreconfig(
                {
                    url: getEnvironmentApiUrl() + 'preconfig/patient_preconfig/' + patientPreconfigID,
                    method: 'GET',
                },
                (data) => {
                    if (!data) {
                        reject(new Error('No patient preconfig detail returned'));
                        return;
                    }

                    const mergedInquiries = [
                        ...(data.preconfig?.preconfig_inquiries ?? []),
                        ...(data.override_preconfig?.preconfig_inquiries ?? [])
                    ];

                    getPatientInquries(
                        {
                            url: getEnvironmentApiUrl() + 'patient_inquiry/all',
                            method: 'POST',
                            body: {
                                params: {},
                                filter: { patient_id: patientID, end_date: null }
                            }
                        },
                        (allPatientInquiries) => {
                            const inquiriesAlreadyAssigned = allPatientInquiries.filter(i =>
                                i.assigned_by_preconfig ||
                                mergedInquiries.some(pi => pi.inquiry_id === i.inquiry_id)
                            );

                            setPatientPreconfigDetail(data);
                            setPatientInquiries(inquiriesAlreadyAssigned);

                            resolve({
                                detail: data,
                                inquiries: inquiriesAlreadyAssigned
                            });
                        },
                        (inquiriesError) => {
                            reject(inquiriesError);
                        }
                    );
                },
                (detailError) => {
                    reject(detailError);
                }
            );
        });
    }, [getPatientInquries, getPatientPreconfig, patientID, patientPreconfigID]);

    useEffect(() => {
        if (patientPreconfigID == null) return;

        fetchPatientPreconfigData().catch((err) => {
            console.log('fetchPatientPreconfigData -> error', err);
        });
    }, [patientPreconfigID, fetchPatientPreconfigData]);

    const refetchPatientPreconfig = useCallback(() => {
        return fetchPatientPreconfigData();
    }, [fetchPatientPreconfigData]);

    const loadingConstants = isLoading;
    const errorConstants = error;
    const loadingInquiries = isLoading || loadingInquiry;
    const errorInquiries = error || errorPatientInquiry;

    const basePreconfigSheetParameters = useMemo(() => {
        return patientPreconfigDetail?.preconfig?.preconfig_sheet_parameters ?? [];
    }, [patientPreconfigDetail]);

    const basePreconfigInquiries = useMemo(() => {
        return patientPreconfigDetail?.preconfig?.preconfig_inquiries ?? [];
    }, [patientPreconfigDetail]);

    const preconfigSheetParameters = useMemo(() => {
        const baseParams = patientPreconfigDetail?.preconfig?.preconfig_sheet_parameters ?? [];
        const overrideParams = patientPreconfigDetail?.override_preconfig?.preconfig_sheet_parameters ?? [];

        const merged = new Map<string, PreconfigSheetParameter>();

        baseParams.forEach(param => {
            if (param.sheet_parameter_id) {
                merged.set(param.sheet_parameter_id, param);
            }
        });

        overrideParams.forEach(param => {
            if (param.sheet_parameter_id) {
                merged.set(param.sheet_parameter_id, param);
            }
        });

        return Array.from(merged.values());
    }, [patientPreconfigDetail]);

    const preconfigInquiries = useMemo(() => {
        const baseInquiries = patientPreconfigDetail?.preconfig?.preconfig_inquiries ?? [];
        const overrideInquiries = patientPreconfigDetail?.override_preconfig?.preconfig_inquiries ?? [];

        const merged = new Map<number, typeof baseInquiries[number]>();

        baseInquiries.forEach(inquiry => {
            merged.set(inquiry.inquiry_id, inquiry);
        });

        overrideInquiries.forEach(inquiry => {
            merged.set(inquiry.inquiry_id, inquiry);
        });

        return Array.from(merged.values());
    }, [patientPreconfigDetail]);

    return {
        loadingConstants,
        errorConstants,
        loadingInquiries,
        errorInquiries,
        basePreconfigSheetParameters,
        basePreconfigInquiries,
        preconfigSheetParameters,
        preconfigInquiries,
        patientInquiries,
        patientPreconfigDetail,
        hasOverride: !!patientPreconfigDetail?.override_preconfig,
        refetchPatientPreconfig,
    };
};

export const useSavePatientPreconfig = (patientPreconfigID?: number) => {
    const { sendRequest } = useHttp();

    const saveOverride = (
        preconfigSheetParameters: Array<
            Pick<
                PreconfigSheetParameter,
                | 'id'
                | 'preconfig_id'
                | 'sheet_parameter_id'
                | 'max_limit'
                | 'min_limit'
                | 'frecuency'
                | 'monthly_frequency_day'
                | 'semanal_frequency'
                | 'frequency_duration'
                | 'reminder_time'
            >
        >,
        preconfigInquiries: Array<
            Pick<
                PatientInquiry,
                | 'id'
                | 'inquiry_id'
                | 'patient_id'
                | 'frecuency'
                | 'monthly_frequency_day'
                | 'semanal_frequency'
                | 'frequency_duration'
                | 'reminder_time'
            >
        >,
        onSuccess?: () => void,
        onError?: (err: unknown) => void
    ) => {
        if (!patientPreconfigID) return;

        const sanitizedPreconfigInquiries = preconfigInquiries.map(
            ({ patient_id, ...inquiry }) => inquiry
        );

        const payload = toPatientPreconfigOverridePayload(
            preconfigSheetParameters as PreconfigSheetParameter[],
            sanitizedPreconfigInquiries
        );

        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/patient_preconfig/' + patientPreconfigID + '/override',
            method: 'PATCH',
            body: payload
        }, () => {
            onSuccess?.();
        }, (err) => {
            console.log('saveOverride -> error', err);
            onError?.(err);
        });
    };

    const deleteOverride = (
        onSuccess?: () => void,
        onError?: (err: unknown) => void
    ) => {
        if (!patientPreconfigID) return;

        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/patient_preconfig/' + patientPreconfigID + '/override',
            method: 'DELETE',
        }, () => {
            onSuccess?.();
        }, (err) => {
            console.log('deleteOverride -> error', err);
            onError?.(err);
        });
    };

    return {
        saveOverride,
        deleteOverride
    };
};

export const usePatientPreconfigParameters = () => {
    const [patientSheetParameters, dispatchPreconfigSheetParams] = useReducer(patientPreconfigReducer, []);

    const restartSheetParameters = useCallback((sheetParameters: PreconfigSheetParameter[]) => {
        dispatchPreconfigSheetParams({
            type: 'SET_DEFAULT',
            sheetParameters,
        });
    }, []);

    return {
        patientSheetParameters,
        restartSheetParameters,
        dispatchPreconfigSheetParams,
    };
};

export const usePatientPreconfigInquiries = (patientID: number) => {
    const [preconfigPatientInquiries, dispatchPreconfigPatientInquiries] = useReducer(
        preconfigPatientInquiriesReducer,
        []
    );

    const handleChangeInquiry = useCallback((
        oldID: number,
        inquiryID: number,
        option: OptionAutocomplete | null,
        poll: Poll | undefined
    ) => {
        if (option === null) {
            dispatchPreconfigPatientInquiries({ type: 'DELETE_INQUIRY', id: oldID });
        } else if (oldID === -1) {
            if (poll) {
                dispatchPreconfigPatientInquiries({
                    type: 'ADD_INQUIRY',
                    inquiryID,
                    poll,
                    patient_id: patientID
                });
            }
        } else if (poll) {
            dispatchPreconfigPatientInquiries({
                type: 'CHANGE_INQUIRY',
                oldID,
                inquiryID,
                poll,
                patient_id: patientID
            });
        }
    }, [patientID]);

    const handleChangeInquiryFreq = useCallback((id: number, freq: PollFrequency) => {
        dispatchPreconfigPatientInquiries({ type: 'CHANGE_FREQUENCY', id, freq });
    }, []);

    const handleChangeInquiryReminder = useCallback((id: number, reminder: string | null) => {
        dispatchPreconfigPatientInquiries({ type: 'CHANGE_REMINDER', id, reminder });
    }, []);

    const restartPatientInquiries = useCallback((preconfigInquiries: PreconfigInquiry[]) => {
        dispatchPreconfigPatientInquiries({
            type: 'SET_DEFAULT',
            inquiries: preconfigInquiries
        });
    }, []);

    return {
        preconfigPatientInquiries,
        handleChangeInquiry,
        handleChangeInquiryFreq,
        handleChangeInquiryReminder,
        restartPatientInquiries,
    };
};

export const useFormPreconfig = (
    sheetParameters: Parameter[],
    dispatchPreconfigSheetParams: React.Dispatch<PreconfigDispatch>
) => {
    const handleChangeConstant = (id: string | null) => (_: unknown, option: OptionAutocomplete | null) => {
        if (option === null) {
            if (id === null) return;

            dispatchPreconfigSheetParams({
                type: 'DELETE_CONSTANT',
                sheetParameterID: id
            });
        } else {
            const sheetParameter = sheetParameters.find(p => p.id === option.id.toString());
            if (!sheetParameter) return;

            dispatchPreconfigSheetParams({
                type: 'ADD_CONSTANT',
                sheetParameterID: option.id.toString(),
                defaultMinLimit: sheetParameter.min_limit,
                defaultMaxLimit: sheetParameter.max_limit
            });
        }
    };

    const handleChangeValue = (id: string, type: 'CHANGE_MIN_LIMIT' | 'CHANGE_MAX_LIMIT') =>
        (evt: React.ChangeEvent<HTMLInputElement>) => {
            const value = evt.target.value.trim() === '' ? null : Number(evt.target.value);
            dispatchPreconfigSheetParams({ type, sheetParameterID: id.toString(), value });
        };

    const handleChangeFreq = (id: string) => (freq: PollFrequency) => {
        dispatchPreconfigSheetParams(
            { type: 'CHANGE_FREQ', sheetParameterID: id.toString(), value: freq }
        );
    };

    const handleChangeReminder = (id: string) => (hours: HH | null, minutes: mm | null) => {
        const value = parseReminderTime(hours, minutes);
        dispatchPreconfigSheetParams(
            { type: 'CHANGE_REMINDER_TIME', sheetParameterID: id.toString(), value }
        );
    };

    return {
        handleChangeConstant,
        handleChangeValue,
        handleChangeFreq,
        handleChangeReminder
    };
};